<template>
  <!-- Add / Edit Expense -->
  <div
    class="modal fade dq-modal"
    id="add_expenses"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content dq-modal-card" :key="keyForce">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge" :class="isEdit ? 'dq-icon-badge-warn' : ''">
              <i :class="isEdit ? 'ti ti-edit' : 'ti ti-cash'"></i>
            </span>

            <div class="lh-1">
              <h4 class="modal-title mb-1">
                {{ isEdit ? "Edit Expense" : "Add Expense" }}
              </h4>
              <div class="dq-subtitle">
                Capture expense details for approvals, reports, and budget tracking.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="expenseStore.loading"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit" novalidate>
          <div class="modal-body dq-modal-body">
            <div class="dq-form-grid">
              <!-- Top row: name + status pill -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Expense Name <span class="text-danger">*</span>
                </label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-id"></i>
                  </span>
                  <input
                    ref="nameInputEl"
                    v-model.trim="form.name"
                    type="text"
                    class="form-control dq-input"
                    placeholder="e.g. Office rent, Fuel, Internet subscription"
                    :aria-invalid="attempted && !!errors.name"
                    autocomplete="off"
                  />
                </div>

                <div class="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                  <div v-if="attempted && errors.name" class="dq-error">
                    <i class="ti ti-alert-circle me-1"></i>{{ errors.name }}
                  </div>
                  <div v-else class="dq-help">
                    Use a clear name your finance team can recognize.
                  </div>

                  <span class="dq-pill" :class="statusPill.cls">
                    <i class="ti ti-circle-check me-1"></i>{{ statusPill.text }}
                  </span>
                </div>
              </div>

              <!-- Grid: date / amount / currency -->
              <div class="dq-three">
                <div class="dq-field">
                  <label class="form-label dq-label">Date</label>
                  <div class="dq-input-wrap">
                    <span class="dq-input-icon"><i class="ti ti-calendar"></i></span>
                    <input v-model="form.expense_date" type="date" class="form-control dq-input" />
                  </div>
                  <div class="dq-help">When the expense occurred.</div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Amount</label>
                  <div class="dq-input-wrap">
                    <span class="dq-input-icon"><i class="ti ti-currency-dollar"></i></span>
                    <input
                      v-model.number="form.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control dq-input dq-input-right"
                      placeholder="0.00"
                      :aria-invalid="attempted && !!errors.amount"
                      @input="normalizeAmount"
                    />
                  </div>
                  <div v-if="attempted && errors.amount" class="dq-error">
                    <i class="ti ti-alert-circle me-1"></i>{{ errors.amount }}
                  </div>
                  <div v-else class="dq-help">Numbers only. Supports decimals.</div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Currency</label>
                  <div class="dq-select-wrap">
                    <span class="dq-input-icon"><i class="ti ti-coin"></i></span>
                    <select v-model="form.currency" class="form-select dq-select">
                      <option value="KES">KES</option>
                      <option value="UGX">UGX</option>
                      <option value="TZS">TZS</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <div class="dq-help">Used in reports and totals.</div>
                </div>
              </div>

              <!-- Category + Payment method -->
              <div class="dq-two">
                <div class="dq-field">
                  <label class="form-label dq-label">Category</label>
                  <div class="dq-input-wrap">
                    <span class="dq-input-icon"><i class="ti ti-tag"></i></span>
                    <input
                      v-model.trim="form.category"
                      type="text"
                      class="form-control dq-input"
                      placeholder="Travel, Office Supplies, Utilities..."
                    />
                  </div>
                  <div class="dq-help">Optional — helps filter and analyze spend.</div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Payment Method</label>
                  <div class="dq-select-wrap">
                    <span class="dq-input-icon"><i class="ti ti-credit-card"></i></span>
                    <select v-model="form.payment_method" class="form-select dq-select">
                      <option value="">Select</option>
                      <option>Cash</option>
                      <option>Bank Transfer</option>
                      <option>Mobile Money</option>
                      <option>Card</option>
                      <option>Cheque</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div class="dq-help">Optional — for reconciliation.</div>
                </div>
              </div>

              <!-- Reference + Payee -->
              <div class="dq-two">
                <div class="dq-field">
                  <label class="form-label dq-label">Reference</label>
                  <div class="dq-input-wrap">
                    <span class="dq-input-icon"><i class="ti ti-file-invoice"></i></span>
                    <input
                      v-model.trim="form.reference"
                      type="text"
                      class="form-control dq-input"
                      placeholder="Invoice / Bill No."
                    />
                  </div>
                  <div class="dq-help">Optional. Useful for audits.</div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Payee</label>
                  <div class="dq-input-wrap">
                    <span class="dq-input-icon"><i class="ti ti-user"></i></span>
                    <input
                      v-model.trim="form.payee"
                      type="text"
                      class="form-control dq-input"
                      placeholder="Person / Vendor"
                    />
                  </div>
                  <div class="dq-help">Optional. Who was paid?</div>
                </div>
              </div>

              <!-- Status -->
              <div class="dq-field">
                <label class="form-label dq-label">Status</label>
                <div class="dq-select-wrap">
                  <span class="dq-input-icon"><i class="ti ti-toggle-left"></i></span>
                  <select v-model="form.status" class="form-select dq-select">
                    <option>Pending</option>
                    <option>Approved</option>
                    <option>Paid</option>
                    <option>Cancelled</option>
                  </select>
                </div>
                <div class="dq-help">
                  <span class="dq-pill" :class="statusPill.cls">{{ statusPill.text }}</span>
                  <span class="ms-2 text-muted">Controls approval and reporting behavior.</span>
                </div>
              </div>

              <!-- Notes -->
              <div class="dq-field">
                <label class="form-label dq-label">Notes</label>
                <textarea
                  v-model.trim="form.notes"
                  class="form-control dq-textarea"
                  rows="3"
                  placeholder="Optional note (reason, context, receipt details...)"
                ></textarea>
                <div class="dq-help">Optional. Keep it short and useful.</div>
              </div>

              <!-- Tip -->
              <div class="dq-tip">
                <div class="dq-tip-icon">
                  <i class="ti ti-bulb"></i>
                </div>
                <div>
                  <div class="dq-tip-title">Quick tip</div>
                  <div class="dq-tip-text">
                    Add a reference (invoice/bill) and payee for easier audits and approvals.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn-soft me-2"
              data-bs-dismiss="modal"
              :disabled="expenseStore.loading"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="expenseStore.loading">
              <span v-if="expenseStore.loading" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="expenseStore.loading">{{ isEdit ? "Saving…" : "Adding…" }}</span>
              <span v-else>{{ isEdit ? "Save Changes" : "Add Expense" }}</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-modal" id="delete_expenses" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge">
              <i class="ti ti-trash-x"></i>
            </span>
          </div>

          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">
            This will permanently delete the expense.
            <span class="d-block mt-1 text-muted small">Tip: Cancelled status is a safer alternative for audit trails.</span>
          </p>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button class="btn btn-light dq-btn-soft" data-bs-dismiss="modal" :disabled="expenseStore.loading">
              Cancel
            </button>

            <button class="btn btn-danger dq-btn-danger" :disabled="expenseStore.loading" @click="confirmDelete">
              <span v-if="expenseStore.loading" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="expenseStore.loading">Deleting…</span>
              <span v-else>Yes, Delete</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { toast } from "@/utils/toast";
import { useExpenseStore } from "@/stores/expenseStore";

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const expenseStore = useExpenseStore();

const keyForce = ref(0);
const form = ref(blankForm());

const attempted = ref(false);
const nameInputEl = ref(null);

const isEdit = computed(() => !!props.editItem?.id);

function blankForm() {
  return {
    id: null,
    name: "",
    expense_date: "",
    amount: 0,
    currency: "KES",
    category: "",
    payment_method: "",
    reference: "",
    payee: "",
    status: "Pending",
    notes: "",
  };
}

function reset() {
  attempted.value = false;
  Object.assign(form.value, blankForm());
  keyForce.value++;
}

function hydrate(val) {
  if (!val) {
    reset();
    return;
  }
  form.value.id = val.id ?? null;
  form.value.name = val.name ?? "";
  form.value.expense_date = val.expense_date ?? "";
  form.value.amount = Number(val.amount ?? 0);
  form.value.currency = val.currency ?? "KES";
  form.value.category = val.category ?? "";
  form.value.payment_method = val.payment_method ?? "";
  form.value.reference = val.reference ?? "";
  form.value.payee = val.payee ?? "";
  form.value.status = val.status ?? "Pending";
  form.value.notes = val.notes ?? "";
  keyForce.value++;
}

watch(
  () => props.editItem,
  (val) => hydrate(val),
  { immediate: true }
);

const errors = computed(() => {
  const e = {};
  if (!form.value.name?.trim()) e.name = "Expense name is required.";
  const amt = Number(form.value.amount);
  if (Number.isNaN(amt)) e.amount = "Amount must be a valid number.";
  else if (amt < 0) e.amount = "Amount cannot be negative.";
  return e;
});

const statusPill = computed(() => {
  const s = String(form.value.status || "").toLowerCase();
  if (s.includes("paid")) return { cls: "dq-pill-success", text: "Paid" };
  if (s.includes("approve")) return { cls: "dq-pill-info", text: "Approved" };
  if (s.includes("cancel")) return { cls: "dq-pill-muted", text: "Cancelled" };
  return { cls: "dq-pill-warn", text: "Pending" };
});

function normalizeAmount() {
  const n = Number(form.value.amount);
  if (Number.isNaN(n)) return;
  if (n < 0) form.value.amount = 0;
}

function hide() {
  const el = document.getElementById("add_expenses");
  const inst = el ? Modal.getInstance(el) || Modal.getOrCreateInstance(el) : null;
  inst?.hide();
}

function hideDelete() {
  const el = document.getElementById("delete_expenses");
  const inst = el ? Modal.getInstance(el) || Modal.getOrCreateInstance(el) : null;
  inst?.hide();
}

function toPayload(f) {
  return {
    name: f.name?.trim(),
    expense_date: f.expense_date || null,
    amount: Number.isFinite(Number(f.amount)) ? Number(f.amount) : 0,
    currency: f.currency || "KES",
    category: f.category?.trim() || null,
    payment_method: f.payment_method || null,
    reference: f.reference?.trim() || null,
    payee: f.payee?.trim() || null,
    status: f.status || "Pending",
    notes: f.notes?.trim() || null,
  };
}

async function submit() {
  attempted.value = true;

  if (errors.value.name) {
    toast(errors.value.name, "error");
    await nextTick();
    nameInputEl.value?.focus?.();
    return;
  }
  if (errors.value.amount) {
    toast(errors.value.amount, "error");
    return;
  }

  const payload = toPayload(form.value);

  try {
    if (isEdit.value && form.value.id) {
      await expenseStore.update(form.value.id, payload);
    } else {
      await expenseStore.create(payload);
    }
    hide();
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save expense", "error");
  }
}

async function confirmDelete() {
  try {
    if (!props.editItem?.id) return;
    await expenseStore.remove(props.editItem.id);
    hideDelete();
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete expense", "error");
  }
}

onMounted(() => {
  const el = document.getElementById("add_expenses");

  el?.addEventListener("show.bs.modal", async () => {
    if (isEdit.value) hydrate(props.editItem);
    else reset();

    await nextTick();
    nameInputEl.value?.focus?.();
  });

  el?.addEventListener("hidden.bs.modal", reset);
});
</script>

<style scoped>
/* ===== DQ Premium Modal System ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 18px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  font-size: 20px;
}
.dq-icon-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-modal-body {
  padding: 18px;
  max-height: 76vh;
  overflow: auto;
}

.dq-form-grid {
  display: grid;
  gap: 14px;
}

.dq-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.dq-three {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
}
@media (max-width: 991px) {
  .dq-two,
  .dq-three {
    grid-template-columns: 1fr;
  }
}

.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap,
.dq-select-wrap {
  position: relative;
}

.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}

.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input-right {
  text-align: right;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

.dq-select {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
}
.dq-select:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-textarea:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

.dq-error {
  margin-top: 6px;
  font-size: 0.82rem;
  font-weight: 800;
  color: rgba(220, 38, 38, 1);
  display: inline-flex;
  align-items: center;
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.75rem;
  border: 1px solid rgba(0,0,0,0.06);
}
.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-pill-info {
  background: rgba(2, 132, 199, 0.10);
  color: rgba(3, 105, 161, 1);
}
.dq-pill-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}
.dq-pill-muted {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(71, 85, 105, 1);
}

/* Tip card */
.dq-tip {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
}
.dq-tip-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.14);
  color: rgba(79, 70, 229, 1);
}
.dq-tip-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-tip-text {
  font-size: 0.83rem;
  color: rgba(17, 24, 39, 0.6);
}

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}
.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}
.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Delete modal polish */
.dq-delete-body {
  padding: 18px;
  text-align: center;
}
.dq-danger-hero {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}
.dq-danger-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title {
  font-weight: 950;
  letter-spacing: -0.02em;
}
.dq-delete-subtitle {
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.92rem;
  line-height: 1.35rem;
}
.dq-btn-danger {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14);
}
</style>
